import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Monitor, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PortalPWA } from "@/components/PortalPWA";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/ui-kit";
import api, { formatApiError } from "@/lib/api";
import ClinicianTerms from "@/components/ClinicianTerms";
import { HOME } from "@/App";

const PORTALS = {
  doctor: { role: "doctor", label: "Doctor", home: "/dashboard", accent: "#1A3A6D", variant: "desktop", register: true, device: "Desktop app", sub: "Order treatments & track outcomes" },
  pro: { role: "physiotherapist", label: "Clinician", home: "/pro", accent: "#2A5298", variant: "desktop", register: true, device: "Web & mobile app", physio: true, sub: "View assigned cases & log sessions" },
  patient: { role: "patient", label: "Patient", home: "/patient", accent: "#1A3A6D", variant: "desktop", register: true, device: "Web & mobile app", sub: "Track recovery & confirm sessions" },
  admin: { role: "admin", label: "Admin", home: "/admin", accent: "#0A1B33", variant: "desktop", register: false, device: "Desktop console", sub: "Platform operations & billing" },
  ops: { role: "ops", label: "Operations", home: "/ops", accent: "#0A1B33", variant: "desktop", register: false, device: "Desktop console", sub: "Assignment, clinician workforce & vendor billing" },
  finance: { role: "finance", label: "Finance", home: "/finance", accent: "#0A1B33", variant: "desktop", register: false, device: "Desktop console", sub: "Invoicing, AR/AP, P&L & profitability" },
};

export default function PortalAuth({ portal }) {
  const p = PORTALS[portal];
  const { user, ready, login, register, verifyEmail, logout } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "", specialty: "", city: "", phone: "", qualification: "", registration_no: "" });
  const [legal, setLegal] = useState(null);
  const [terms, setTerms] = useState({ a1: false, a2: false, b: false });
  const clinSignup = mode === "register" && p.physio;
  useEffect(() => {
    if (clinSignup && !legal) api.get("/legal/clinician").then((r) => setLegal(r.data)).catch(() => {});
  }, [clinSignup, legal]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [verify, setVerify] = useState(null); // { email } once a doctor must confirm their email
  const [otp, setOtp] = useState("");
  const expired = new URLSearchParams(window.location.search).get("expired");

  if (ready && user) return <Navigate to={HOME[user.role] || "/"} replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const afterAuth = (u) => {
    const okRole = u.role === "superadmin" || u.role === p.role || (portal === "doctor" && u.role === "staff");
    if (!okRole) { setErr(`That account can't access the ${p.label} portal. Please use the correct portal.`); logout(); return; }
    if (portal === "doctor" && u.role === "doctor" && u.credential_status !== "Verified") {
      window.location.assign("/doctor/onboarding");
      return;
    }
    if (u.role === "physiotherapist" && u.credential_status !== "Verified") {
      toast.info("Your account is pending PranIQ Admin verification before you can be matched.", { duration: 8000 });
    }
    // Hard nav: HOME[role] may differ from this portal's home (e.g. superadmin) — the
    // ready&&user Navigate above would otherwise race and win.
    window.location.assign(p.home);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    if (mode === "login") {
      const res = await login(f.email, f.password, remember);
      setLoading(false);
      if (!res.ok) return setErr(res.error);
      toast.success(`Welcome to ${p.label}`);
      return afterAuth(res.user);
    }
    const extra = p.physio ? { qualification: f.qualification, registration_no: f.registration_no, accept_terms: terms.a1, accept_declarations: terms.b,
      marketing_opt_in: terms.a2, legal_version: legal?.version, legal_hash: legal?.hash } : {};
    const res = await register({ name: f.name, email: f.email, password: f.password, role: p.role, specialty: f.specialty, city: f.city, phone: f.phone, ...extra });
    setLoading(false);
    if (!res.ok) return setErr(res.error);
    if (res.verifyEmail) {
      setVerify({ email: res.email });
      if (res.devOtp) toast.info(`Your verification code (preview): ${res.devOtp}`, { duration: 15000 });
      else toast.success("We've emailed you a 6-digit verification code.");
      return;
    }
    toast.success("Account created");
    return afterAuth(res.user);
  };

  const submitVerify = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    const res = await verifyEmail(verify.email, otp);
    setLoading(false);
    if (!res.ok) return setErr(res.error);
    if (p.physio) {
      toast.success("Email verified — your account is ready. A copy of your accepted agreement is in Settings.");
      window.location.assign("/pro");
      return;
    }
    toast.success("Email verified — upload your documents to continue.");
    window.location.assign("/doctor/onboarding");
  };

  const resend = async () => {
    setErr("");
    try {
      const { data } = await api.post("/auth/resend-verification", { email: verify.email });
      if (data.dev_otp) toast.info(`New code (preview): ${data.dev_otp}`, { duration: 15000 });
      else toast.success("A new code has been sent to your email.");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Could not resend code"); }
  };

  const verifyCard = (
    <form onSubmit={submitVerify} className="w-full">
      <div className="flex items-center justify-center mb-2"><Logo height={38} /></div>
      <div className="text-center mb-5">
        <div className="inline-flex items-center gap-1.5 pq-badge bg-slate-100 text-slate-600 mb-2"><ShieldCheck size={12} /> Email verification</div>
        <h1 className="font-display text-xl font-bold text-slate-900">Verify your email</h1>
        <p className="text-slate-500 text-[13px]">Enter the 6-digit code we sent to <span className="font-semibold text-slate-700">{verify?.email}</span></p>
      </div>
      <Field label="Verification code">
        <input required inputMode="numeric" maxLength={6} className="pq-input tracking-[0.4em] text-center text-lg" value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="verify-otp-input" placeholder="000000" />
      </Field>
      {err && <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2 mt-1" data-testid="verify-error">{err}</div>}
      <button type="submit" disabled={loading || otp.length < 6} data-testid="verify-submit" className="pq-btn w-full py-3 mt-3 text-white text-[14.5px]" style={{ background: p.accent, borderColor: p.accent, color: "#ffffff" }}>
        {loading ? <Spinner className="w-4 h-4" /> : <>Verify & continue <ArrowRight size={17} /></>}
      </button>
      <p className="text-center text-[13px] text-slate-500 mt-4">
        Didn't get it? <button type="button" className="font-semibold" style={{ color: p.accent }} onClick={resend} data-testid="verify-resend">Resend code</button>
      </p>
      <button type="button" onClick={() => { setVerify(null); setOtp(""); setErr(""); }} className="flex items-center justify-center gap-1.5 text-[12.5px] text-slate-400 mt-5 hover:text-slate-600 w-full" data-testid="verify-back"><ArrowLeft size={13} /> Back to sign up</button>
    </form>
  );

  const card = (
    <form onSubmit={submit} className="w-full">
      <div className="flex items-center justify-center mb-2"><Logo height={38} /></div>
      <div className="text-center mb-5">
        <div className="inline-flex items-center gap-1.5 pq-badge bg-slate-100 text-slate-600 mb-2">
          <Monitor size={12} />{p.device}
        </div>
        <h1 className="font-display text-xl font-bold text-slate-900">{p.label} {mode === "login" ? "sign in" : "sign up"}</h1>
        <p className="text-slate-500 text-[13px]">{p.sub}</p>
      </div>

      {mode === "register" && <Field label="Full name"><input required className="pq-input" value={f.name} onChange={set("name")} data-testid="auth-name" placeholder="Your name" /></Field>}
      <Field label="Email"><input required type="email" className="pq-input" value={f.email} onChange={set("email")} data-testid="auth-email" placeholder="you@email.com" /></Field>
      <Field label="Password">
        <div className="relative">
          <input required type={showPw ? "text" : "password"} className="pq-input pr-10" value={f.password} onChange={set("password")} data-testid="auth-password" placeholder="••••••••" />
          <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="toggle-password-visibility" aria-label={showPw ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </Field>
      {mode === "login" && (
        <div className="flex items-center justify-between -mt-1.5 mb-1">
          <label className="flex items-center gap-1.5 text-[12.5px] text-slate-600 cursor-pointer select-none" data-testid="remember-me">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-teal-600" /> Remember me
          </label>
          <Link to="/reset" className="text-[12.5px] font-semibold" style={{ color: p.accent }} data-testid="forgot-password-link">Forgot password?</Link>
        </div>
      )}
      {mode === "register" && p.physio && (
        <>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Specialty"><input className="pq-input" value={f.specialty} onChange={set("specialty")} placeholder="e.g. Sports Rehab" data-testid="auth-specialty" /></Field>
          <Field label="City"><input className="pq-input" value={f.city} onChange={set("city")} placeholder="City" data-testid="auth-city" /></Field>
          <Field label="Qualification"><input required className="pq-input" value={f.qualification} onChange={set("qualification")} placeholder="e.g. BPT, MPT" data-testid="auth-qualification" /></Field>
          <Field label="Registration no."><input required className="pq-input" value={f.registration_no} onChange={set("registration_no")} placeholder="Council / registration no." data-testid="auth-registration" /></Field>
        </div>
        <ClinicianTerms legal={legal} value={terms} onChange={setTerms} />
        </>
      )}
      {expired && !err && <div className="text-[12.5px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-1" data-testid="session-expired-note">Your session ended. Please sign in again.</div>}
      {err && <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2 mt-1" data-testid="auth-error">{err}</div>}
      <button type="submit" disabled={loading || (clinSignup && (!legal || !terms.a1 || !terms.b))} data-testid="auth-submit" className="pq-btn w-full py-3 mt-3 text-white text-[14.5px] disabled:opacity-50" style={{ background: p.accent, borderColor: p.accent, color: "#ffffff" }}>
        {loading ? <Spinner className="w-4 h-4" /> : <>{mode === "login" ? "Sign In" : clinSignup ? "Accept and Create Account" : "Create Account"} <ArrowRight size={17} /></>}
      </button>
      {p.register && (
        <p className="text-center text-[13px] text-slate-500 mt-4">
          {mode === "login" ? "New here? " : "Have an account? "}
          <button type="button" className="font-semibold" style={{ color: p.accent }} onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} data-testid="auth-toggle">
            {mode === "login" ? "Create account" : "Sign in"}
          </button>
        </p>
      )}
      {portal === "admin" && (
        <Link to="/finance/login" className="flex items-center justify-center gap-1.5 text-[12.5px] font-semibold mt-3 hover:underline" style={{ color: p.accent }} data-testid="finance-login-link">
          Finance Admin? Sign in to the Finance portal <ArrowRight size={13} />
        </Link>
      )}
      <Link to="/" className="flex items-center justify-center gap-1.5 text-[12.5px] text-slate-400 mt-5 hover:text-slate-600"><ArrowLeft size={13} /> All portals</Link>
    </form>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "radial-gradient(circle at 15% 20%, rgba(42,82,152,.14), transparent 45%), radial-gradient(circle at 85% 80%, rgba(218,165,32,.12), transparent 45%), linear-gradient(180deg,#f6f8fc,#eef2fa)" }}>
      <PortalPWA portal={portal} showBanner={false} />
      <div className="w-full max-w-[420px] bg-white rounded-3xl p-9 border border-slate-200" style={{ boxShadow: "0 20px 60px rgba(10,27,51,.16)" }}>{verify ? verifyCard : card}</div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">{label}</span>{children}</label>;
}
